/**
 * The G1a Control Center.
 *
 * Shows what the backend actually implements, and says so when a value is not
 * available rather than inventing one. The one action with real consequence is
 * approving or rejecting a proposed external write, and the approve control is
 * deliberately explicit: the reviewer must see the diff and acknowledge it.
 */

import { useCallback, useEffect, useState } from "react";
import {
  api,
  Approval,
  ApprovalDetail,
  ApprovalPolicy,
  ApiError,
  AuditEntry,
  AuditVerifyResult,
  BudgetSummary,
  CostEntry,
  CostSummary,
  Digest,
  formatMicros,
  Membership,
  OrgUser,
  Overview,
  Project,
} from "@/api/client";
import { PERMISSION, useSession } from "@/control/SessionContext";
import { ResearchPanel } from "@/control/ResearchReview";
import { OpportunityPanel } from "@/control/OpportunityReview";
import { BusinessFlowPanel } from "@/control/BusinessFlowPanel";
import { MinbarPilotPanel, canShowMinbarPilotPanel } from "@/control/MinbarPilotResults";
import { controlDataScopeLabel } from "@/control/controlDataScope";

import { IntegrationsPanel } from "./IntegrationsPanel";
import { CapabilityMatrix } from "./CapabilityMatrix";
import { CoordinatorPanel } from "./CoordinatorPanel";
import { WorkRegister } from "./WorkRegister";
import { JobsPanel } from "./JobsPanel";
import { UsageGuide } from "./UsageGuide";
import { SessionSettings } from "./SessionSettings";
import { ControlShell } from "./ControlShell";
import { DashboardHome } from "./DashboardHome";
import { buildDecisionDraft } from "./decisionDraft";
import { menuFor } from "./menuFor";
import { resolveBrand } from "./brand";
import { ControlThemeSelect } from "./ControlThemeSelect";
import { LeadSearchPanel } from "./LeadSearchPanel";
import { ApprovalDecision } from "./ApprovalDecision";
import { approvalLabel } from "./describeApproval";

type Panel = "integrations" | "coordinator" | "work" | "guide" | "session" | "overview" | "pilot" | "jobs" | "research" | "opportunity" | "business"
  | "approvals" | "budget" | "audit" | "governance" | "leads";
type Lang = "hr" | "en";

const PANELS: { id: Panel; label: string; labelEn: string }[] = [
  { id: "overview", label: "Pregled", labelEn: "Overview" },
  { id: "jobs", label: "Poslovi", labelEn: "Jobs" },
  { id: "research", label: "Istraživanje", labelEn: "Research" },
  { id: "opportunity", label: "Oportuniteti", labelEn: "Opportunity Analyst" },
  { id: "business", label: "Poslovni tokovi", labelEn: "Business flows" },
  { id: "approvals", label: "Odobrenja", labelEn: "Approvals" },
  { id: "budget", label: "Budžet", labelEn: "Budget" },
  { id: "audit", label: "Audit", labelEn: "Audit" },
  { id: "governance", label: "Upravljanje", labelEn: "Governance" },
];

const PILOT_PANEL = {
  id: "pilot" as const,
  label: "Rezultati pilota",
  labelEn: "Pilot results",
};

const PROGRAM_LABEL: Record<Project["program"], string> = {
  megabooker_sales_growth: "A · MegaBooker Sales & Growth",
  zoho_hotel_solutions: "B · Zoho Hotel Solutions",
  minbarlive_growth: "C · MinbarLive Growth",
};

function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const text = error instanceof ApiError ? error.messageHr : String(error);
  return (
    <p className="rounded border border-destructive/40 bg-destructive/10 p-3 text-sm"
       role="alert" data-testid="control-error">
      {text}
    </p>
  );
}

export default function ControlCenterPage() {
  const { user, projects, project, selectProject, signOut, can, idleMinutes, setIdleMinutes } = useSession();
  const [panel, setPanel] = useState<Panel>("overview");
  const [lang, setLang] = useState<Lang>("hr");
  const [draft, setDraft] = useState<string | null>(null);
  const ownerPilotAvailable = canShowMinbarPilotPanel(user, project);
  const pilotPanelActive = panel === "pilot" && ownerPilotAvailable;
  const chatAvailable = Boolean(user?.is_org_owner);
  const workAvailable = Boolean(user?.is_org_owner && project?.id === "prj_minbarlive");
  const GUIDE_PANEL = {id: "guide" as const, label: "Kako koristiti", labelEn: "How to use"};
  const SESSION_PANEL = {id: "session" as const, label: "Sesija", labelEn: "Session"};
  const integrationPanels = user?.is_org_owner ? [{id: "integrations" as const, label: "Integrations", labelEn: "Integrations"}] : [];
  const leadPanels = user?.is_org_owner ? [{id: "leads" as const, label: "Pronađi leadove", labelEn: "Find leads"}] : [];
  const visiblePanels = workAvailable
    ? [
        ...(chatAvailable ? [{id: "coordinator" as const, label: "Glavni agent", labelEn: "Main agent"}] : []),
        {id: "work" as const, label: "Roadmap i stvarni rad", labelEn: "Roadmap & real work"},
        PANELS[0], PILOT_PANEL, ...integrationPanels, ...leadPanels, ...PANELS.slice(1), SESSION_PANEL, GUIDE_PANEL]
    : chatAvailable
      ? [
          {id: "coordinator" as const, label: "Glavni agent", labelEn: "Main agent"},
          ...integrationPanels, ...leadPanels, ...PANELS, SESSION_PANEL, GUIDE_PANEL]
      : [...integrationPanels, ...leadPanels, ...PANELS, SESSION_PANEL, GUIDE_PANEL];
  useEffect(() => { setPanel(chatAvailable ? "coordinator" : "overview"); }, [project?.id, chatAvailable]);

  useEffect(() => {
    if (panel === "pilot" && !ownerPilotAvailable) setPanel("overview");
  }, [ownerPilotAvailable, panel]);

  const panelView = projects.length > 0 ? (
          <div className={process.env.REACT_APP_UI_SHELL === "1" && panel === "coordinator" ? "flex min-h-0 flex-1 flex-col" : "mt-6"}>
            {panel === "integrations" && user?.is_org_owner && <><CapabilityMatrix key={`cm:${user!.id}:${project!.id}`} projectId={project!.id} actorId={user!.id}/><IntegrationsPanel key={`${user!.id}:${project!.id}`} projectId={project!.id} actorId={user!.id}/></>}
            {panel === "coordinator" && chatAvailable && <CoordinatorPanel key={`${user!.id}:${project!.id}`} projectId={project!.id} actorId={user!.id} draft={draft ?? undefined} onDraftUsed={() => setDraft(null)} projectName={project!.name} projects={projects.map((p) => ({ id: p.id, name: p.name, agent: true }))} onSelectProject={selectProject} lang={lang}/>}
            {panel === "work" && workAvailable && <WorkRegister key={`${user!.id}:${project!.id}`} projectId={project!.id}/>}
            {panel === "guide" && <UsageGuide/>}
            {panel === "session" && <SessionSettings idleMinutes={idleMinutes} onChange={(minutes) => void setIdleMinutes(minutes)}/>}
            {panel === "leads" && user?.is_org_owner && <LeadSearchPanel key={`${user!.id}:${project!.id}`} projectId={project!.id} actorId={user!.id}/>}
            {panel === "overview" && <OverviewPanel projectId={project!.id} dashboard={process.env.REACT_APP_UI_SHELL === "1" ? { lang, program: project?.program, onOpen: (p) => setPanel(p as Panel) } : undefined} />}
            {panel === "pilot" && ownerPilotAvailable && (
              <MinbarPilotPanel projectId={project!.id} lang={lang} />
            )}
            {panel === "jobs" && (
              <JobsPanel key={`${user!.id}:${project!.id}`} projectId={project!.id} canStart={can(PERMISSION.jobStart)}
                         canCancel={can(PERMISSION.jobCancel)}
                         canCancelOwn={can(PERMISSION.jobCancelOwn)}
                         currentUserId={user!.id} />
            )}
            {panel === "research" && <ResearchPanel projectId={project!.id} />}
            {panel === "opportunity" && (
              <OpportunityPanel projectId={project!.id}
                                canStart={can(PERMISSION.jobStart)}
                                currentUserId={user!.id}
                                canManageFields={Boolean(user?.is_org_owner)
                                  || project?.my_role === "project_admin"} />
            )}
            {panel === "business" && (
              <BusinessFlowPanel
                projectId={project!.id}
                canStart={can(PERMISSION.jobStart)}
                onOpenApprovals={() => setPanel("approvals")}
                decisions={{ api, canDecideMedium: can(PERMISSION.approvalMedium), canDecideHigh: can(PERMISSION.approvalHigh), canDecideOwnerOnly: Boolean(user?.is_org_owner), currentUserId: user!.id, budgetApi: { get: () => api.budget(project!.id), initialize: () => api.initializeMonthlyBudget(project!.id), resume: () => api.resumeWaitingRuns(project!.id) } }}
              />
            )}
            {panel === "approvals" && (
              <ApprovalsPanel
                projectId={project!.id}
                canDecideMedium={can(PERMISSION.approvalMedium)}
                canDecideHigh={can(PERMISSION.approvalHigh)}
                canDecideOwnerOnly={Boolean(user?.is_org_owner)}
                currentUserId={user!.id}
                onDiscuss={process.env.REACT_APP_UI_SHELL === "1" && chatAvailable ? (d) => { setDraft(buildDecisionDraft(d, lang)); setPanel("coordinator"); } : undefined}
              />
            )}
            {panel === "budget" && <BudgetPanel projectId={project!.id} />}
            {panel === "audit" && <AuditPanel projectId={project!.id} />}
            {panel === "governance" && (
              <GovernancePanel
                projectId={project!.id}
                isOrgOwner={Boolean(user?.is_org_owner)}
              />
            )}
          </div>
  ) : null;

  if (process.env.REACT_APP_UI_SHELL === "1") {
    return (
      <div data-testid="control-center">
        <ControlShell
          brand={resolveBrand()}
          sections={menuFor({ isOrgOwner: Boolean(user?.is_org_owner), workAvailable, chatAvailable, pilotAvailable: ownerPilotAvailable })}
          labelFor={(id) => { const e = visiblePanels.find((x) => x.id === id); return e ? (lang === "en" ? e.labelEn : e.label) : id; }}
          activePanel={panel}
          onSelectPanel={(id) => setPanel(id as Panel)}
          lang={lang}
          onToggleLang={() => setLang((prev) => (prev === "hr" ? "en" : "hr"))}
          themeControl={<ControlThemeSelect />}
          headerRight={
            <>
              <span className="hidden text-sm text-muted-foreground md:inline" data-testid="control-data-scope">
                {user?.display_name} ·{" "}
                {panel === "coordinator" && chatAvailable ? "Stvarni razgovor · DeepSeek koordinator" : panel === "work" && workAvailable ? "Stvarni vanjski izvještaji · prioriteti projekta" : controlDataScopeLabel(lang, pilotPanelActive)}
              </span>
              <select
                value={project?.id ?? ""}
                onChange={(event) => selectProject(event.target.value)}
                className="min-w-0 flex-1 truncate rounded border border-border bg-background px-2 py-1.5 text-sm md:mt-1 md:flex-none md:px-3 md:py-2"
                aria-label={lang === "en" ? "Project" : "Projekt"}
                data-testid="control-project-select"
              >
                {projects.length === 0 && (
                  <option value="">
                    {lang === "en" ? "No accessible projects" : "Nema dostupnih projekata"}
                  </option>
                )}
                {projects.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name || PROGRAM_LABEL[entry.program]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => void signOut()}
                className="shrink-0 rounded border border-border px-3 py-1.5 text-sm md:py-2"
                data-testid="control-signout"
              >
                {lang === "en" ? "Sign out" : "Odjava"}
              </button>
            </>
          }
        >
          {projects.length === 0 ? (
            <p className="mt-8 rounded border border-border p-6 text-sm text-muted-foreground"
               data-testid="control-no-projects">
              Ovaj račun nema dodijeljen nijedan projekt. Projektni putovi vraćaju 404 dok
              članstvo ne postoji, i to je namjerno.
            </p>
          ) : (
            <>
              {user?.is_org_owner && project?.id !== "prj_minbarlive" && projects.some(p => p.id === "prj_minbarlive") && (
                <div className="mt-5 rounded-xl border border-primary/30 p-4"><strong>Tražiš MinbarLive rezultate?</strong><p className="mt-1 text-sm text-muted-foreground">Istraživanje i CRM izvještaji imaju zaseban prikaz, odvojen od testnih poslova.</p><button className="mt-3 underline" onClick={()=>selectProject("prj_minbarlive")}>Otvori MinbarLive glavnog agenta →</button></div>
              )}
              {panelView}
            </>
          )}
        </ControlShell>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6" data-testid="control-center">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            MegaBooker AI Business OS · G1b
          </p>
          <h1 className="mt-2 text-2xl font-semibold">Control Center</h1>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="control-data-scope">
            {user?.display_name} · {user?.is_org_owner ? "org_owner" : "član projekta"} ·{" "}
            {panel === "coordinator" && chatAvailable ? "Stvarni razgovor · DeepSeek koordinator" : panel === "work" && workAvailable ? "Stvarni vanjski izvještaji · prioriteti projekta" : controlDataScopeLabel(lang, pilotPanelActive)}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <button
            type="button"
            onClick={() => setLang((prev) => (prev === "hr" ? "en" : "hr"))}
            className="rounded border border-border px-2.5 py-2 font-mono text-xs uppercase"
            data-testid="control-lang-toggle"
          >
            {lang === "hr" ? "HR / EN" : "EN / HR"}
          </button>
          <label className="block">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              {lang === "en" ? "Project" : "Projekt"}
            </span>
            <select
              value={project?.id ?? ""}
              onChange={(event) => selectProject(event.target.value)}
              className="mt-1 rounded border border-border bg-background px-3 py-2 text-sm"
              data-testid="control-project-select"
            >
              {projects.length === 0 && (
                <option value="">
                  {lang === "en" ? "No accessible projects" : "Nema dostupnih projekata"}
                </option>
              )}
              {projects.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {PROGRAM_LABEL[entry.program]} — {entry.my_role}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void signOut()}
            className="rounded border border-border px-3 py-2 text-sm"
            data-testid="control-signout"
          >
            {lang === "en" ? "Sign out" : "Odjava"}
          </button>
        </div>
      </header>

      {projects.length === 0 ? (
        <p className="mt-8 rounded border border-border p-6 text-sm text-muted-foreground"
           data-testid="control-no-projects">
          Ovaj račun nema dodijeljen nijedan projekt. Projektni putovi vraćaju 404 dok
          članstvo ne postoji, i to je namjerno.
        </p>
      ) : (
        <>
          {user?.is_org_owner && project?.id !== "prj_minbarlive" && projects.some(p => p.id === "prj_minbarlive") && (
            <div className="mt-5 rounded-xl border border-primary/30 p-4"><strong>Tražiš MinbarLive rezultate?</strong><p className="mt-1 text-sm text-muted-foreground">Istraživanje i CRM izvještaji imaju zaseban prikaz, odvojen od testnih poslova.</p><button className="mt-3 underline" onClick={()=>selectProject("prj_minbarlive")}>Otvori MinbarLive glavnog agenta →</button></div>
          )}
          <nav className="mt-6 flex flex-wrap gap-2 border-b border-border pb-2">
            {visiblePanels.map((entry) => (
              <button
                key={entry.id}
                type="button"
                onClick={() => setPanel(entry.id)}
                className={`rounded px-3 py-1.5 text-sm ${
                  panel === entry.id
                    ? "bg-primary text-primary-foreground"
                    : "border border-border"
                }`}
                data-testid={`control-tab-${entry.id}`}
              >
                {lang === "en" ? entry.labelEn : entry.label}
              </button>
            ))}
          </nav>

          {panelView}
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ overview */

function OverviewPanel({ projectId, dashboard }: { projectId: string; dashboard?: { lang: "hr" | "en"; program?: string; onOpen: (panel: string) => void } }) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { can } = useSession();
  const [killing, setKilling] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.projectOverview(projectId));
    } catch (err) {
      setError(err);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleSwitch(engaged: boolean) {
    setKilling(true);
    try {
      await api.setKillSwitch(projectId, engaged, engaged ? "Control Center" : undefined);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setKilling(false);
    }
  }

  if (!data) return dashboard ? <div><ErrorNote error={error} /><DashboardHome overview={null} {...dashboard} /></div> : <><ErrorNote error={error} /><p className="text-sm text-muted-foreground">Učitavanje…</p></>;

  const cards: { label: string; value: string; note?: string }[] = [
    { label: "U redu", value: String(data.jobs.queued ?? 0) },
    { label: "U izvršenju", value: String(data.jobs.running ?? 0) },
    { label: "Čeka odobrenje", value: String(data.jobs.awaiting_approval ?? 0),
      note: data.pending_approvals > 0 ? `${data.pending_approvals} otvoreno` : undefined },
    { label: "Čeka budžet", value: String(data.jobs.awaiting_budget_approval ?? 0) },
    { label: "Za pregled", value: String(data.jobs.needs_review ?? 0),
      note: "nepoznat ishod" },
    { label: "Uspješno", value: String(data.jobs.succeeded ?? 0) },
    { label: "Neuspješno", value: String(data.jobs.failed ?? 0) },
    { label: "Audit zapisa", value: String(data.audit_records) },
  ];

  const body = (
    <div className="space-y-6">
      <ErrorNote error={error} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className="rounded border border-border p-4"
               data-testid={`control-metric-${card.label}`}>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value}</p>
            {card.note && <p className="text-xs text-muted-foreground">{card.note}</p>}
          </div>
        ))}
      </div>

      {data.budget && (
        <section className="rounded border border-border p-4">
          <h2 className="text-sm font-semibold">Budžet {data.budget.period}</h2>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
            <div><dt className="text-muted-foreground">Potrošeno</dt>
              <dd>{formatMicros(data.budget.spent_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Rezervirano</dt>
              <dd>{formatMicros(data.budget.reserved_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Dostupno</dt>
              <dd>{formatMicros(data.budget.available_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Tvrdi limit</dt>
              <dd>
                {data.budget.hard_limit_micros === null
                  ? "nije postavljen"
                  : formatMicros(data.budget.hard_limit_micros)}
              </dd></div>
          </dl>
          {data.budget.hard_limit_micros === null ? (
            <p className="mt-3 text-xs text-warn" data-testid="budget-guarantee-na">
              Nije primjenjivo — limit nije postavljen, pa se tvrdi cap ne jamči.
            </p>
          ) : data.budget.hard_limit_guaranteed ? (
            <p className="mt-3 text-xs text-muted-foreground"
               data-testid="budget-guarantee-true">
              Tvrdi limit je tehnički zajamčen (nema zabilježenog prekoračenja).
            </p>
          ) : (
            <p className="mt-3 text-xs text-destructive" data-testid="budget-guarantee-broken">
              Tvrdi limit više nije tehnički zajamčen
              {data.budget.guarantee_broken_at ? ` (od ${data.budget.guarantee_broken_at})` : ""}.
            </p>
          )}
          {data.budget.frozen_at && (
            <p className="mt-2 text-xs text-destructive">
              Budžet je zamrznut: {data.budget.frozen_reason}.
            </p>
          )}
        </section>
      )}

      <section className="rounded border border-border p-4">
        <h2 className="text-sm font-semibold">Kill switch</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Projekt: {data.kill_switch.project_engaged ? "uključen" : "isključen"} ·
          Globalni: {data.kill_switch.global_engaged ? "uključen" : "isključen"}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Uključivanje poništava sva otvorena odobrenja i zaustavlja nova. Isključivanje
          ih ne vraća.
        </p>
        {can(PERMISSION.killSwitchProject) && !data.kill_switch.global_engaged ? (
          <button
            type="button"
            disabled={killing}
            onClick={() => void toggleSwitch(!data.kill_switch.project_engaged)}
            className="mt-3 rounded border border-destructive/50 px-3 py-1.5 text-sm text-destructive disabled:opacity-50"
            data-testid="control-kill-switch-toggle"
          >
            {data.kill_switch.project_engaged ? "Isključi kill switch" : "Uključi kill switch"}
          </button>
        ) : (
          <p className="mt-3 text-xs text-muted-foreground"
             data-testid="control-kill-switch-locked">
            {data.kill_switch.global_engaged
              ? "Globalni kill switch je uključen; projektni se ne može mijenjati."
              : "Nemate ovlast za projektni kill switch."}
          </p>
        )}
      </section>
    </div>
  );
  return dashboard ? <DashboardHome overview={data} {...dashboard}>{body}</DashboardHome> : body;
}

/* ---------------------------------------------------------------------- jobs */

/* ----------------------------------------------------------------- approvals */

function ApprovalsPanel({ projectId, canDecideMedium, canDecideHigh, canDecideOwnerOnly, currentUserId, onDiscuss }: {
  projectId: string; canDecideMedium: boolean; canDecideHigh: boolean;
  canDecideOwnerOnly: boolean;
  currentUserId: string; onDiscuss?: (detail: ApprovalDetail) => void;
}) {
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setApprovals((await api.listApprovals(projectId)).items);
      setSelectedId(null);
      setDetail(null);
    } catch (err) {
      setError(err);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  function open(approvalId: string) {
    setSelectedId(approvalId);
    setDetail(null);
  }

  return (
    <div className="space-y-4">
      <ErrorNote error={error} />
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="hidden py-2 sm:table-cell">Odobrenje</th><th>Što</th>
            <th className="hidden sm:table-cell">Rizik</th><th>Status</th>
            <th className="hidden sm:table-cell">Glasovi</th><th /></tr>
        </thead>
        <tbody>
          {approvals.map((approval) => (
            <tr key={approval.id} className="border-b border-border/50">
              <td className="hidden py-2 font-mono text-xs sm:table-cell">{approval.id}</td>
              <td data-testid={`approval-what-${approval.id}`} className="py-2 pr-2 text-sm break-words">{approvalLabel(approval.action_type)}</td>
              <td data-testid={`approval-risk-${approval.id}`} className="hidden sm:table-cell">{approval.risk}</td>
              <td>{approval.status}</td>
              <td className="hidden sm:table-cell">{approval.approvals_recorded} / {approval.required_approvals}</td>
              <td className="py-2 text-right">
                <button type="button" className="min-h-11 underline" onClick={() => void open(approval.id)}
                        data-testid={`approval-open-${approval.id}`}>
                  Pregledaj
                </button>
              </td>
            </tr>
          ))}
          {approvals.length === 0 && (
            <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
              Nema odobrenja.
            </td></tr>
          )}
        </tbody>
      </table>

      {selectedId && (
        <div className="space-y-3">
          <ApprovalDecision
            key={selectedId}
            projectId={projectId}
            approvalId={selectedId}
            currentUserId={currentUserId}
            canDecideMedium={canDecideMedium}
            canDecideHigh={canDecideHigh}
            canDecideOwnerOnly={canDecideOwnerOnly}
            onLoaded={setDetail}
            onDecided={() => void load()}
          />
          {onDiscuss && detail && (
            <button type="button" className="min-h-11 rounded border border-border px-3 text-sm" data-testid="approval-discuss" onClick={() => onDiscuss(detail)}>Razradi s agentom</button>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- budget */

function BudgetPanel({ projectId }: { projectId: string }) {
  const [budget, setBudget] = useState<BudgetSummary | null>(null);
  const [costSum, setCostSum] = useState<CostSummary | null>(null);
  const [costs, setCosts] = useState<CostEntry[]>([]);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    (async () => {
      setError(null);
      try {
        setBudget(await api.budget(projectId));
        setCostSum(await api.costSummary(projectId));
        setCosts((await api.listCosts(projectId)).items);
      } catch (err) {
        setError(err);
      }
    })();
  }, [projectId]);

  return (
    <div className="space-y-4">
      <ErrorNote error={error} />
      {budget && (
        <section className="rounded border border-border p-4">
          <h2 className="text-sm font-semibold">Razdoblje {budget.period}</h2>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div><dt className="text-muted-foreground">Potrošeno</dt>
              <dd>{formatMicros(budget.spent_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Rezervirano</dt>
              <dd>{formatMicros(budget.reserved_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Meki limit</dt>
              <dd>{formatMicros(budget.soft_limit_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Tvrdi limit</dt>
              <dd>{budget.hard_limit_micros === null ? "nije postavljen"
                : formatMicros(budget.hard_limit_micros)}</dd></div>
            <div><dt className="text-muted-foreground">Nepoznat trošak</dt>
              <dd>{budget.unavailable_count}</dd></div>
            <div><dt className="text-muted-foreground">Otvorene rezervacije</dt>
              <dd>{budget.open_reservations}</dd></div>
            {costSum && (
              <>
                <div><dt className="text-muted-foreground">Stvarni (actual)</dt>
                  <dd data-testid="cost-summary-actual">{formatMicros(costSum.actual_micros)}</dd></div>
                <div><dt className="text-muted-foreground">Procijenjeni (estimated)</dt>
                  <dd data-testid="cost-summary-estimated">{formatMicros(costSum.estimated_micros)}</dd></div>
              </>
            )}
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Trošak koji provider ne javi knjiži se po granici rezervacije i broji kao
            nepoznat; nikad se ne knjiži kao nula.
          </p>
        </section>
      )}

      <table className="w-full text-left text-sm">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="py-2">Stavka</th><th>Tip</th><th>Status</th><th>Iznos</th>
            <th>Prekoračenje</th></tr>
        </thead>
        <tbody>
          {costs.map((entry) => (
            <tr key={entry.id} className="border-b border-border/50">
              <td className="py-2 font-mono text-xs">{entry.reservation_id ?? entry.id}</td>
              <td>{entry.entry_type}</td>
              <td>{entry.cost_status ?? "—"}</td>
              <td>{formatMicros(entry.amount_micros)}</td>
              <td>{formatMicros(entry.overrun_micros)}</td>
            </tr>
          ))}
          {costs.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">
              Nema knjiženih troškova.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------------------------------- audit */

function AuditPanel({ projectId }: { projectId: string }) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [verifyResult, setVerifyResult] = useState<AuditVerifyResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    (async () => {
      setError(null);
      try {
        setEntries((await api.listAudit(projectId)).items);
        setVerifyResult(await api.verifyAudit(projectId));
      } catch (err) {
        setError(err);
      }
    })();
  }, [projectId]);

  return (
    <div className="space-y-4">
      <ErrorNote error={error} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Zapisuje isključivo outbox relay. Svaki zapis nosi hash prethodnog zapisa, pa je
          izmjenu moguće dokazati. Odluka je u auditu prije bilo kakvog vanjskog učinka.
        </p>
        {verifyResult && (
          <span
            className={`rounded border px-2.5 py-1 font-mono text-xs ${
              verifyResult.ok ? "border-border" : "border-destructive text-destructive"
            }`}
            data-testid="audit-verify-badge"
          >
            {verifyResult.ok
              ? `Lanac verificiran (${verifyResult.checked_entries} zapisa)`
              : `Neispravan lanac na seq ${verifyResult.first_bad_seq}: ${verifyResult.reason}`}
          </span>
        )}
      </div>
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
          <tr><th className="py-2">#</th><th>Radnja</th><th>Aktor</th><th>Vrijeme</th>
            <th>Hash</th></tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-b border-border/50">
              <td className="py-2">{entry.seq}</td>
              <td>{entry.action}</td>
              <td className="font-mono text-xs">{entry.actor.id}</td>
              <td className="text-xs">{entry.ts}</td>
              <td className="font-mono text-xs">{entry.hash.slice(0, 18)}…</td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">
              Nema audit zapisa u ovom lancu.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------------------------- governance */

function GovernancePanel({
  projectId,
  isOrgOwner,
}: {
  projectId: string;
  isOrgOwner: boolean;
}) {
  const [digest, setDigest] = useState<Digest | null>(null);
  const [members, setMembers] = useState<Membership[]>([]);
  const [policy, setPolicy] = useState<ApprovalPolicy | null>(null);
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    (async () => {
      setError(null);
      try {
        setDigest(await api.projectDigest(projectId));
        setMembers(await api.listMembers(projectId));
        setPolicy(await api.getApprovalPolicy());
        if (isOrgOwner) {
          setOrgUsers(await api.listOrgUsers());
        }
      } catch (err) {
        setError(err);
      }
    })();
  }, [projectId, isOrgOwner]);

  return (
    <div className="space-y-6" data-testid="control-governance">
      <ErrorNote error={error} />

      {digest && (
        <section className="rounded border border-border p-4" data-testid="governance-digest">
          <h2 className="text-sm font-semibold">Dnevni pregled ({digest.date})</h2>
          <dl className="mt-3 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-muted-foreground">Poslovi (uspješno / pregled)</dt>
              <dd>{digest.jobs.succeeded} / {digest.jobs.needs_review}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Odobrenja (owner override)</dt>
              <dd>{digest.approvals.approved} ({digest.approvals.owner_overrides})</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Stvarni / Procijenjeni trošak</dt>
              <dd>
                {formatMicros(digest.cost.actual_micros)} /{" "}
                {formatMicros(digest.cost.estimated_micros)}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Audit sidro</dt>
              <dd className="font-mono text-xs">
                {digest.audit_anchor
                  ? `seq ${digest.audit_anchor.seq}`
                  : "nema zapisa"}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {policy && (
        <section className="rounded border border-border p-4" data-testid="governance-policy">
          <h2 className="text-sm font-semibold">
            Politika odobravanja (v{policy.version})
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Istek: {policy.rules.expiry_hours} h · High 2. osoba:{" "}
            {policy.rules.high.second_approver ? "da" : "ne"} · Critical 2 osobe:{" "}
            {policy.rules.critical.two_person ? "da" : "ne"}
          </p>
        </section>
      )}

      <section className="rounded border border-border p-4" data-testid="governance-members">
        <h2 className="text-sm font-semibold">Članstva na projektu ({members.length})</h2>
        <ul className="mt-2 space-y-1 text-sm">
          {members.map((m) => (
            <li key={m.id} className="font-mono text-xs">
              {m.user_id} → {m.role}
            </li>
          ))}
        </ul>
      </section>

      {isOrgOwner && orgUsers.length > 0 && (
        <section className="rounded border border-border p-4" data-testid="governance-users">
          <h2 className="text-sm font-semibold">Korisnici organizacije ({orgUsers.length})</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {orgUsers.map((u) => (
              <li key={u.id} className="font-mono text-xs">
                {u.id} ({u.email}) · {u.status} · TOTP: {u.totp_enabled ? "da" : "ne"}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
